import "./StudentManagement.scss";
import { SearchBar } from "components/form";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CustomModal, PopupModal } from "components/modals/ModalBox";
import Pagination from "react-js-pagination";
import useFullPageLoader from "common/UseFullPageLoader";
import useDebounce from "common/UseDebounce";
import NotFound from "common/NotFound";
import { userPlaceholder } from "assets/icons";
import findSerialNumber from "utils/findSerialNumber";
import { limitOption } from "constants/staticData";
import { firstWordCapital } from "utils/common";
import { Select } from "common/InputForm";
import { activityListApi, deleteActivityApi } from "services/activity";
import TypographyLimit from "components/TypographyLimit";
import ManageActivity from "./ManageActivity";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "sonner";
import useUser from "hooks/useUser";

// language selector component import

import ActivityManagementTabs from "./ActivityManagementTabs";

const formatCategories = {
  "MOTOR-SKILLS": "Motor Skills",
  "SENSORY-PLAN": "Sensory Plan",
  SOCIAL_SKILLS: "Social Skills",
  "COGNITIVE-DEVELOPMENT": "Cognitive Development",
};

const ActivityManagement = () => {
  const { havePermission } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [imagePreview, setImagePreview] = useState("");
  const [showImagePreviewModel, setShowImagePreviewModel] = useState(false);
  const [userList, setUserList] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 300);
  const [loader, onShow, onHide] = useFullPageLoader();
  const userTypeValue = searchParams.get("userType") || "";
  const activePage = +searchParams.get("page") || 1;
  const limits = +searchParams.get("limit") || 10;
  const [total, setTotal] = useState(null);
  const [state, setState] = useState({
    manage: {
      isOpen: false,
      type: "",
      _id: "",
    },
    delete: {
      isOpen: false,
      _id: "",
    },
    status: "",
    category: "",
    ageGroup: "",
  });

  // language selector state
  const [propertyTab, setPropertyTab] = useState("en");

  // show image preview handler
  const showImagePreviewHandler = (profile) => {
    setShowImagePreviewModel(true);
    setImagePreview(profile);
  };

  // Handle pagination
  const handlePageChange = (event) => {
    searchParams.set("page", event);
    setSearchParams(searchParams);
  };

  // handle limit of page
  const handleLimit = (event) => {
    searchParams.set("limit", event.target.value);
    searchParams.delete("page");
    setSearchParams(searchParams);
  };

  // user list handler
  const userListHandler = async (activePage, limits) => {
    try {
      onShow();
      const reqData = {
        pagination: "true",
        page: activePage,
        limit: limits,
        language: propertyTab,
        // isAdminCreated: true,
        ...(search && debouncedValue && { search: debouncedValue }),
        ...(state.status && { status: state.status }),
        ...(state.category && { category: state.category }),
        ...(state.ageGroup && { ageGroup: state.ageGroup }),
      };
      const { data, status } = await activityListApi(reqData);
      if (status === 200) {
        setTotal(data?.meta.totalRecords);
        setUserList(data?.data);
      }
    } catch (e) {
      console.log(e);
    } finally {
      onHide();
    }
  };

  useEffect(() => {
    if (Number(activePage) === 1) {
      searchParams.delete("page");
      setSearchParams(searchParams);
    }
    userListHandler(activePage, limits);
  }, [
    searchParams,
    activePage,
    debouncedValue,
    userTypeValue,
    limits,
    state.category,
    state.ageGroup,
    propertyTab,
  ]);

  useEffect(() => {
    if (debouncedValue) {
      searchParams.delete("page");
    }
  }, [debouncedValue, propertyTab]);

  const handleDelete = async () => {
    try {
      const { data } = await deleteActivityApi({ _id: state.delete._id });
      if (data?.success) {
        toast.success(data?.message);
        setState((prev) => ({ ...prev, delete: { isOpen: false, _id: "" } }));
        userListHandler(activePage, limits);
      } else {
        toast.error(data?.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div className="student_management">
        <div className="dashboard_title has_child">
          <h3>Activities Management</h3>
          {havePermission("activity.write") && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setState((prev) => ({
                  ...prev,
                  manage: {
                    isOpen: true,
                    type: "CREATE",
                  },
                }));
              }}
            >
              Add Activity
            </button>
          )}
        </div>
        <div className="button_section_wrap">
          <div className="search_wrapper">
            <SearchBar
              placeholder={"Search"}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
            />

            <Select
              name="category"
              onChange={(e) =>
                setState((prev) => ({ ...prev, category: e.target.value }))
              }
              value={state.category}
            >
              <option value="">category</option>
              <option value="MOTOR-SKILLS">Motor Skills</option>
              <option value="SENSORY-PLAN">Sensory Plan</option>
              <option value="SOCIAL_SKILLS">Social Skills</option>
              <option value="COGNITIVE-DEVELOPMENT">
                Cognitive Development
              </option>
            </Select>
            <Select
              name="ageGroup"
              onChange={(e) =>
                setState((prev) => ({ ...prev, ageGroup: e.target.value }))
              }
              value={state.ageGroup}
            >
              <option value="">Age Group</option>
              <option value="0-3">0-3 Months</option>
              <option value="4-6">4-6 Months</option>
              <option value="7-9">7-9 Months</option>
              <option value="10-12">10-12 Months</option>
              <option value="13-15">13-15 Months</option>
              <option value="16-18">16-18 Months</option>
              <option value="19-21">19-21 Months</option>
              <option value="22-24">22-24 Months</option>
            </Select>
          </div>
        </div>

        {/* ----------- language selector */}

        {/* <ActivityManagementTabs
          setPropertyTab={setPropertyTab}
          propertyTab={propertyTab}
        /> */}

        <div className="card">
          <div className="card-body">
            <div className="table_wrap ">
              <table className="table custom_table">
                <thead>
                  <tr>
                    <th className="nowrap text-center">S.No</th>
                    <th className="nowrap text-center">Name</th>
                    <th className="nowrap text-center">Image</th>
                    <th className="nowrap text-center">Category</th>
                    <th className="nowrap text-center">Description</th>
                    <th className="nowrap text-center">User Views</th>
                    <th className="nowrap text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loader}
                  {userList && userList.length ? (
                    userList?.map((data, index) => {
                      return (
                        <tr key={index}>
                          <td className="nowrap text-center">
                            {findSerialNumber(index, activePage, limits)}
                          </td>

                          <td className="nowrap text-center text-capitalize">
                            {firstWordCapital(data?.name || "---")}
                          </td>
                          <td className="nowrap text-center">
                            <img
                              src={
                                data?.image?.length && data?.image?.length > 50
                                  ? data?.image
                                  : userPlaceholder
                              }
                              alt="user-profile-image"
                              className="avatar"
                              onClick={() =>
                                showImagePreviewHandler(data?.image)
                              }
                            />
                          </td>
                          <td className="nowrap text-center">
                            {formatCategories[data?.category]}
                          </td>
                          <td className="nowrap text-center">
                            <TypographyLimit
                              text={data?.description}
                              limit={15}
                            />
                          </td>
                          <td className="nowrap text-center">
                            {firstWordCapital(data?.completedCount || 0)}
                          </td>
                          <td>
                            <div className="icon_wrap">
                              {havePermission("activity.write") && (
                                <IconButton
                                  onClick={() => {
                                    setState((prev) => ({
                                      ...prev,
                                      manage: {
                                        isOpen: true,
                                        type: "EDIT",
                                        _id: data?._id,
                                      },
                                    }));
                                  }}
                                >
                                  <EditIcon color="primary" />
                                </IconButton>
                              )}
                              {havePermission("activity.delete") && (
                                <IconButton
                                  onClick={() => {
                                    setState((prev) => ({
                                      ...prev,
                                      delete: {
                                        isOpen: true,
                                        _id: data?._id,
                                      },
                                    }));
                                  }}
                                >
                                  <DeleteIcon color="error" />
                                </IconButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <NotFound
                      cols={10}
                      msg={"User Data not found"}
                      onHide={onHide}
                    />
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination-wrapper">
              <div className="Pagination">
                <select
                  className="input form-control"
                  value={limits}
                  onChange={handleLimit}
                >
                  {limitOption &&
                    limitOption.length > 0 &&
                    limitOption.map((limitValue) => (
                      <option key={limitValue.value} value={limitValue.value}>
                        {limitValue.label}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            {total > limits && (
              <div className="pagination-wrapper">
                <div className="Pagination">
                  <Pagination
                    activePage={activePage}
                    previousLabel={"previous"}
                    nextLabel={"next"}
                    itemsCountPerPage={limits}
                    totalItemsCount={total}
                    pageRangeDisplayed={5}
                    onChange={handlePageChange}
                    hideDisabled={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview model */}
      <CustomModal
        className={"sm image_preview_modal"}
        show={showImagePreviewModel}
        handleClose={() => {
          setImagePreview("");
          setShowImagePreviewModel(false);
        }}
      >
        <div className="image_preview">
          <img
            src={imagePreview || userPlaceholder}
            alt="user-profile-preview"
          />
        </div>
      </CustomModal>

      {/* Add Edit */}
      <ManageActivity
        _id={state.manage._id}
        type={state.manage.type}
        isOpen={state.manage.isOpen}
        propertyTab={propertyTab}
        getListHandler={() => userListHandler(activePage, limits)}
        onClose={() =>
          setState((prev) => ({ ...prev, manage: { isOpen: false } }))
        }
      />

      {/* Delete Modal */}
      <PopupModal
        show={state.delete.isOpen}
        title={"Are you sure you want to delete this activity?"}
        children={
          <div className="button_wrap">
            <button
              type="button"
              className="button"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  delete: { isOpen: false, _id: "" },
                }))
              }
            >
              Cancel
            </button>
            <button
              type="button"
              className="button light"
              onClick={handleDelete}
            >
              Sure
            </button>
          </div>
        }
      />
    </>
  );
};

export default ActivityManagement;
